"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const serviceTypes = [{
  value: "Installation",
  label: "Install"
}, {
  value: "Repair",
  label: "Repair"
}, {
  value: "Maintenance",
  label: "Service"
}, {
  value: "Estimate",
  label: "Quote"
}, {
  value: "Emergency",
  label: "Urgent"
}];
function TickEyebrow({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-3">
      <span aria-hidden="true" className="inline-block h-3 w-[1.5px]" style={{
      backgroundColor: "var(--primary)"
    }} />
      <span className="font-sans text-xs font-semibold uppercase" style={{
      color: "var(--primary)",
      letterSpacing: "0.22em"
    }}>
        {label}
      </span>
      <span aria-hidden="true" className="inline-block h-3 w-[1.5px]" style={{
      backgroundColor: "var(--primary)"
    }} />
    </div>;
}
function ServiceRequestForm() {
  const [form, setForm] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("Request received. A pro will contact you shortly.");
  const [ackChecked, setAckChecked] = useState(false);
  const [hpValue, setHpValue] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    service_type: "",
    service_address: "",
    message: ""
  });
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, any>>({});
  const fetchTimeRef = useRef<number>(Date.now());
  const loadFormDefinition = async () => {
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        setLoadError("error");
        return;
      }
      let data: any = null;
      try {
        data = await client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request");
      } catch (err: any) {
        if (err?.status === 404) {
          const list = await client.pluginRequest("wvc-forms/v1/forms");
          const items = list?.items || [];
          const matched = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : null) || items.find((f: any) => /service|request|booking/i.test(f.name || f.key));
          if (matched?.key) {
            data = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          } else {
            setLoadError("missing");
            return;
          }
        } else {
          setLoadError("error");
          return;
        }
      }
      if (data) {
        setForm(data);
        fetchTimeRef.current = Date.now();
      }
    } catch {
      setLoadError("error");
    }
  };
  useEffect(() => {
    loadFormDefinition();
  }, []);
  const serverErrorFor = (key: string) => {
    if (!form?.fields) return "";
    const fieldId = form.fields.find((f: any) => f.key === key)?.id;
    if (fieldId && serverFieldErrors[fieldId]?.message) {
      return serverFieldErrors[fieldId].message;
    }
    return "";
  };
  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      service_type: "",
      service_address: "",
      message: ""
    });
    setAckChecked(false);
    setClientErrors({});
    setServerFieldErrors({});
    setSubmitError("");
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const errs: Record<string, string> = {};
    if (!formData.full_name.trim()) errs.full_name = "Please enter your full name.";
    if (!formData.phone.trim()) errs.phone = "Please enter your phone number.";
    if (!formData.email.trim()) {
      errs.email = "Please enter a valid email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    if (!formData.service_address.trim()) {
      errs.service_address = "Please enter the service address.";
    }
    if (!formData.service_type.trim()) {
      errs.service_type = "Please select a service type.";
    }
    if (!formData.message.trim()) {
      errs.message = "Please describe the problem.";
    } else if (formData.message.trim().length < 10) {
      errs.message = "Please provide at least 10 characters.";
    }
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      return;
    }
    setClientErrors({});
    setServerFieldErrors({});
    setSubmitError("");
    setIsSubmitting(true);
    const minSeconds = form.spam?.min_seconds ?? 3;
    const elapsed = (Date.now() - fetchTimeRef.current) / 1000;
    if (elapsed < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsed) * 1000)));
    }
    const getFieldId = (key: string) => form.fields?.find((f: any) => f.key === key)?.id;
    const values: Record<string, any> = {};
    const fnId = getFieldId("full_name");
    if (fnId) values[fnId] = formData.full_name.trim();
    const emailId = getFieldId("email");
    if (emailId) values[emailId] = formData.email.trim();
    const phoneId = getFieldId("phone");
    if (phoneId) values[phoneId] = formData.phone.trim();
    const stId = getFieldId("service_type");
    if (stId) values[stId] = formData.service_type;
    const saId = getFieldId("service_address");
    if (saId) values[saId] = formData.service_address.trim();
    const msgId = getFieldId("message");
    if (msgId) values[msgId] = formData.message.trim();
    const appTypeId = getFieldId("appliance_type");
    if (appTypeId) values[appTypeId] = "";
    const spId = getFieldId("source_page");
    if (spId) values[spId] = "Electrical Services";
    const scId = getFieldId("source_city");
    if (scId) values[scId] = "";
    const ssId = getFieldId("source_service");
    if (ssId) values[ssId] = "Electrical Services";
    try {
      const client = getWvcClient();
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${form.key}/submit`, {
        method: "POST",
        data: {
          values,
          token: form.spam?.token,
          hp: form.spam?.honeypot ? {
            [form.spam.honeypot.field]: hpValue
          } : undefined,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res?.success) {
        setIsSubmitted(true);
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else if (res.confirmation?.message) {
          setSuccessMessage(res.confirmation.message);
        }
      } else if (res?.code === "frms_validation_failed") {
        setServerFieldErrors(res.errors ?? {});
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else if (res?.code === "frms_invalid_token") {
        await loadFormDefinition();
        setSubmitError("Session expired. Please submit again.");
      } else {
        setSubmitError(res?.message || "Error sending request. Please call us.");
      }
    } catch {
      setSubmitError("Error sending request. Please call us.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="rounded-2xl p-10 text-center" style={{
      backgroundColor: "var(--card)",
      border: "1px solid var(--border)"
    }}>
        <h3 className="mb-3 font-mono text-2xl font-bold uppercase" style={{
        color: "var(--foreground)"
      }}>
          {"Request Sent"}
        </h3>
        <div className="mb-6 font-sans text-base" style={{
        color: "var(--muted-foreground)"
      }} dangerouslySetInnerHTML={{
        __html: successMessage
      }} />
        <Button onClick={resetForm} className="h-14 rounded-lg px-8 font-sans text-sm font-semibold uppercase" style={{
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)",
        letterSpacing: "0.06em"
      }}>
          {"New Request"}
        </Button>
      </div>;
  }
  if (loadError) {
    return <div className="rounded-2xl p-10 text-center" style={{
      backgroundColor: "var(--card)",
      border: "1px solid var(--border)"
    }}>
        <p className="font-sans text-base" style={{
        color: "var(--muted-foreground)"
      }}>
          {"Unable to load the request form right now. Please call us directly."}
        </p>
      </div>;
  }
  const serviceField = form?.fields?.find((f: any) => f.key === "service_type");
  const optionsToRender = serviceField?.options && serviceField.options.length > 0 ? serviceField.options : serviceTypes;
  return <div className="rounded-2xl p-8 md:p-12" style={{
    backgroundColor: "var(--card)",
    border: "1px solid var(--border)"
  }}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {form?.spam?.honeypot && <input type="text" name={form.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} style={{
        position: "absolute",
        left: "-9999px"
      }} tabIndex={-1} autoComplete="off" aria-hidden="true" />}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="font-sans text-xs font-semibold uppercase block mb-2" style={{
            color: "var(--muted-foreground)",
            letterSpacing: "0.14em"
          }}>
              {"Full Name"}
            </label>
            <Input value={formData.full_name} onChange={e => {
            setFormData(prev => ({
              ...prev,
              full_name: e.target.value
            }));
            if (clientErrors.full_name) {
              setClientErrors(prev => ({
                ...prev,
                full_name: ""
              }));
            }
          }} placeholder="Jane Smith" className="h-14 rounded-md px-5" style={{
            backgroundColor: "var(--background)",
            borderColor: clientErrors.full_name || serverErrorFor("full_name") ? "var(--destructive)" : "var(--border)",
            color: "var(--foreground)"
          }} />
            {(clientErrors.full_name || serverErrorFor("full_name")) && <p className="text-destructive text-sm mt-1">
                {clientErrors.full_name || serverErrorFor("full_name")}
              </p>}
          </div>

          <div>
            <label className="font-sans text-xs font-semibold uppercase block mb-2" style={{
            color: "var(--muted-foreground)",
            letterSpacing: "0.14em"
          }}>
              {"Phone"}
            </label>
            <Input type="tel" value={formData.phone} onChange={e => {
            setFormData(prev => ({
              ...prev,
              phone: e.target.value
            }));
            if (clientErrors.phone) {
              setClientErrors(prev => ({
                ...prev,
                phone: ""
              }));
            }
          }} placeholder="(555) 000-0000" className="h-14 rounded-md px-5" style={{
            backgroundColor: "var(--background)",
            borderColor: clientErrors.phone || serverErrorFor("phone") ? "var(--destructive)" : "var(--border)",
            color: "var(--foreground)"
          }} />
            {(clientErrors.phone || serverErrorFor("phone")) && <p className="text-destructive text-sm mt-1">
                {clientErrors.phone || serverErrorFor("phone")}
              </p>}
          </div>
        </div>

        <div>
          <label className="font-sans text-xs font-semibold uppercase block mb-2" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Email"}
          </label>
          <Input type="email" value={formData.email} onChange={e => {
          setFormData(prev => ({
            ...prev,
            email: e.target.value
          }));
          if (clientErrors.email) {
            setClientErrors(prev => ({
              ...prev,
              email: ""
            }));
          }
        }} placeholder="jane@email.com" className="h-14 rounded-md px-5" style={{
          backgroundColor: "var(--background)",
          borderColor: clientErrors.email || serverErrorFor("email") ? "var(--destructive)" : "var(--border)",
          color: "var(--foreground)"
        }} />
          {(clientErrors.email || serverErrorFor("email")) && <p className="text-destructive text-sm mt-1">
              {clientErrors.email || serverErrorFor("email")}
            </p>}
        </div>

        <div>
          <label className="font-sans text-xs font-semibold uppercase block mb-2" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Service Address"}
          </label>
          <Input value={formData.service_address} onChange={e => {
          setFormData(prev => ({
            ...prev,
            service_address: e.target.value
          }));
          if (clientErrors.service_address) {
            setClientErrors(prev => ({
              ...prev,
              service_address: ""
            }));
          }
        }} placeholder="Street address, city, state, ZIP" className="h-14 rounded-md px-5" style={{
          backgroundColor: "var(--background)",
          borderColor: clientErrors.service_address || serverErrorFor("service_address") ? "var(--destructive)" : "var(--border)",
          color: "var(--foreground)"
        }} />
          {(clientErrors.service_address || serverErrorFor("service_address")) && <p className="text-destructive text-sm mt-1">
              {clientErrors.service_address || serverErrorFor("service_address")}
            </p>}
        </div>

        <div>
          <label className="font-sans text-xs font-semibold uppercase block mb-2" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Service"}
          </label>
          <div className="flex flex-wrap gap-2">
            {optionsToRender.map((svc: any, i: number) => {
            const active = formData.service_type === svc.value;
            return <Button key={svc.value} data-index={i} type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                service_type: svc.value
              }));
              if (clientErrors.service_type) {
                setClientErrors(prev => ({
                  ...prev,
                  service_type: ""
                }));
              }
            }} className="h-11 rounded-lg px-5 font-sans text-sm font-medium transition-colors" style={{
              backgroundColor: active ? "var(--primary)" : "transparent",
              color: active ? "var(--primary-foreground)" : "var(--foreground)",
              border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`
            }}>
                  {svc.label}
                </Button>;
          })}
          </div>
          {(clientErrors.service_type || serverErrorFor("service_type")) && <p className="text-destructive text-sm mt-1">
              {clientErrors.service_type || serverErrorFor("service_type")}
            </p>}
        </div>

        <div>
          <label className="font-sans text-xs font-semibold uppercase block mb-2" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.14em"
        }}>
            {"Describe the Problem"}
          </label>
          <Textarea rows={4} value={formData.message} onChange={e => {
          setFormData(prev => ({
            ...prev,
            message: e.target.value
          }));
          if (clientErrors.message) {
            setClientErrors(prev => ({
              ...prev,
              message: ""
            }));
          }
        }} placeholder="Tell us what your AC, HVAC system, or appliance is doing..." className="min-h-32 rounded-md px-5 py-3" style={{
          backgroundColor: "var(--background)",
          borderColor: clientErrors.message || serverErrorFor("message") ? "var(--destructive)" : "var(--border)",
          color: "var(--foreground)"
        }} />
          {(clientErrors.message || serverErrorFor("message")) && <p className="text-destructive text-sm mt-1">
              {clientErrors.message || serverErrorFor("message")}
            </p>}
        </div>

        <div className="flex items-start gap-3 rounded-md p-4" style={{
        backgroundColor: "var(--background)",
        border: "1px solid var(--border)"
      }}>
          <Checkbox id="emergency-ack" checked={ackChecked} onCheckedChange={v => setAckChecked(Boolean(v))} className="mt-0.5" />
          <label htmlFor="emergency-ack" className="cursor-pointer font-sans text-sm" style={{
          color: "var(--foreground)"
        }}>
            {"I confirm this request."}
          </label>
        </div>

        {submitError && <div className="rounded-md p-4" style={{
        backgroundColor: "color-mix(in srgb, var(--destructive) 10%, transparent)",
        border: "1.5px solid var(--destructive)"
      }}>
            <p className="font-sans text-sm" style={{
          color: "var(--destructive)"
        }}>
              {submitError}
            </p>
          </div>}

        <Button type="submit" disabled={isSubmitting || !ackChecked} className="group h-16 w-full rounded-lg font-sans text-sm font-semibold uppercase" style={{
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)",
        letterSpacing: "0.06em"
      }}>
          <span>{isSubmitting ? "Sending..." : "Submit Request"}</span>
          <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </form>
    </div>;
}
export default function Booking() {
  const reduce = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduce ? 0 : 0.09
      }
    }
  };
  const itemVariants = {
    hidden: reduce ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: reduce ? 0 : 0.6,
        ease: [0.2, 0.7, 0.2, 1] as [number, number, number, number]
      }
    }
  };
  return <section data-section-id="24663" id="bookingrequest" data-nav="light" className="w-full py-28 md:py-36 bg-background text-foreground">
      <div className="mx-auto w-full max-w-[1360px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          <motion.div className="lg:col-span-5" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }}>
            <motion.div variants={itemVariants}>
              <TickEyebrow label="Book Service" />
            </motion.div>

            <motion.h2 variants={itemVariants} className="mt-8 font-mono font-bold uppercase" style={{
            color: "var(--foreground)",
            fontSize: "clamp(2.5rem, 5.6vw, 5rem)",
            lineHeight: 0.96,
            letterSpacing: "-0.005em"
          }}>
              {"Get Help Today."}
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-8 font-sans" style={{
            color: "color-mix(in srgb, var(--foreground) 90%, transparent)",
            fontSize: "clamp(1.125rem, 1.35vw, 1.3125rem)",
            lineHeight: 1.55
          }}>
              {"Connect with independent local pros for your home electrical needs."}
            </motion.p>

            <motion.div variants={itemVariants} className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Phone className="mt-1 size-5 shrink-0" style={{
                color: "var(--foreground)"
              }} strokeWidth={1.75} />
                <div className="flex flex-col">
                  <span className="font-sans text-xs font-medium uppercase" style={{
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.14em"
                }}>
                    {"Call Us"}
                  </span>
                  <a href="tel:+14708479076" className="mt-1 font-sans text-base font-medium underline underline-offset-4 decoration-2" style={{
                  color: "var(--foreground)",
                  textDecorationColor: "var(--primary)"
                }}>
                    (470) 847-9076
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="mt-1 size-5 shrink-0" style={{
                color: "var(--foreground)"
              }} strokeWidth={1.75} />
                <div className="flex flex-col">
                  <span className="font-sans text-xs font-medium uppercase" style={{
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.14em"
                }}>
                    {"Email"}
                  </span>
                  <span className="mt-1 font-sans text-base font-medium" style={{
                  color: "var(--foreground)"
                }}>
                    <a className="underline cursor-pointer" href="mailto:handyheroesinc@gmail.com">
                      handyheroesinc@gmail.com
                    </a>
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin className="mt-1 size-5 shrink-0" style={{
                color: "var(--foreground)"
              }} strokeWidth={1.75} />
                <div className="flex flex-col">
                  <span className="font-sans text-xs font-medium uppercase" style={{
                  color: "var(--muted-foreground)",
                  letterSpacing: "0.14em"
                }}>
                    {"HQ"}
                  </span>
                  <span className="mt-1 font-sans text-base font-medium" style={{
                  color: "var(--foreground)"
                }}>
                    11175 Cicero Drive, Suite 100, Alpharetta GA 30022
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-12 flex flex-wrap gap-3">
              <span className="inline-flex items-center rounded-md px-3 py-1.5 font-sans text-xs font-semibold uppercase" style={{
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              letterSpacing: "0.14em"
            }}>
                {"Verified Pros"}
              </span>
              <span className="inline-flex items-center rounded-md px-3 py-1.5 font-sans text-xs font-semibold uppercase" style={{
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              letterSpacing: "0.14em"
            }}>
                {"Local Experts"}
              </span>
            </motion.div>
          </motion.div>

          <motion.div className="lg:col-span-7" initial={reduce ? {
          opacity: 1
        } : {
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: reduce ? 0 : 0.5,
          ease: "easeOut"
        }}>
            <ServiceRequestForm />
          </motion.div>
        </div>
      </div>
    </section>;
}